import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useParams } from 'react-router-dom'
import { SHELL_TOKENS, text } from '../../shell/styles'
import EmptyState from '../../components/EmptyState'
import MetricCard from '../../components/MetricCard'
import ProjectSurface from './ProjectSurface'
import { useProject } from '../../hooks/useProjects'
import { useSite } from '../../hooks/useSite'
import { useSitePages } from '../../hooks/useSitePages'
import { useSiteBlocks } from '../../hooks/useSiteBlocks'
import { api, ApiError } from '../../api/client'

// Minimum viable Site Builder editor block-type palette. The
// schema accepts arbitrary block_type strings, but constraining
// the create-action menu to a small honest set keeps the UX
// understandable until a richer block library lands.
const BLOCK_TYPES: Array<{ id: string; label: string }> = [
  { id: 'hero', label: 'Hero' },
  { id: 'text', label: 'Text' },
  { id: 'feature', label: 'Feature' },
  { id: 'cta', label: 'Call to action' },
  { id: 'image', label: 'Image' },
  { id: 'spacer', label: 'Spacer' },
]

// Pull the most useful editable string out of the block's
// content_json. The schema is open-ended, so we walk a tiny set
// of conventional keys (title/heading/text/body/copy) and fall
// back to a JSON dump if nothing common is present.
function readBlockContent(content: Record<string, unknown> | undefined): {
  title: string
  body: string
} {
  if (!content) return { title: '', body: '' }
  const title =
    (typeof content.title === 'string' && content.title) ||
    (typeof content.heading === 'string' && content.heading) ||
    (typeof content.label === 'string' && content.label) ||
    ''
  const body =
    (typeof content.body === 'string' && content.body) ||
    (typeof content.text === 'string' && content.text) ||
    (typeof content.copy === 'string' && content.copy) ||
    (typeof content.description === 'string' && content.description) ||
    ''
  return { title, body }
}

function writeBlockContent(
  prev: Record<string, unknown> | undefined,
  patch: { title?: string; body?: string },
): Record<string, unknown> {
  // Preserve any unknown keys that the editor doesn't understand
  // so future block_types with richer content_json don't lose
  // data when an operator just tweaks the title.
  const next: Record<string, unknown> = { ...(prev || {}) }
  if (patch.title !== undefined) next.title = patch.title
  if (patch.body !== undefined) next.body = patch.body
  return next
}

export default function ProjectSite() {
  const { id } = useParams<{ id: string }>()
  const { project } = useProject(id)
  const { site, loading, creating, error, initialize } = useSite(id, project?.name)
  const { pages, loading: pagesLoading, error: pagesError } = useSitePages(site?.id)
  const [selectedPageId, setSelectedPageId] = useState<string | undefined>(undefined)
  const { blocks, loading: blocksLoading, error: blocksError, refresh: refreshBlocks } =
    useSiteBlocks(selectedPageId)
  const [assetCount, setAssetCount] = useState<number | null>(null)
  const [deploymentCount, setDeploymentCount] = useState<number | null>(null)
  const [totalBlockCount, setTotalBlockCount] = useState<number | null>(null)

  // Editor state
  const [busyBlockId, setBusyBlockId] = useState<string | null>(null)
  const [editorError, setEditorError] = useState<string | null>(null)
  const [editorInfo, setEditorInfo] = useState<string | null>(null)
  const [draftContent, setDraftContent] = useState<Record<string, { title: string; body: string }>>({})
  const [addingType, setAddingType] = useState<string>(BLOCK_TYPES[0]?.id ?? 'text')
  const [adding, setAdding] = useState(false)

  // Publish state
  const [publishing, setPublishing] = useState(false)
  const [publishMessage, setPublishMessage] = useState<string | null>(null)

  // Reset editor draft state whenever blocks reload from the server
  // so the inputs always reflect the canonical row content.
  useEffect(() => {
    const next: Record<string, { title: string; body: string }> = {}
    for (const block of blocks) {
      next[block.id] = readBlockContent(block.content_json)
    }
    setDraftContent(next)
  }, [blocks])

  const refreshDeployments = useCallback(async () => {
    if (!site?.id) return
    try {
      const result = await api.site.deployments(site.id, 50)
      setDeploymentCount(result.deployments?.length ?? 0)
    } catch {
      // refresh is best-effort; don't blow away the page on a hiccup
    }
  }, [site?.id])

  function describeError(err: unknown): string {
    if (err instanceof ApiError) {
      return err.message ? `${err.code}: ${err.message}` : err.code
    }
    if (err instanceof Error) return err.message || 'site_block_action_failed'
    return 'site_block_action_failed'
  }

  const onUpdateContent = useCallback(
    async (blockId: string) => {
      const block = blocks.find((b) => b.id === blockId)
      if (!block) return
      const draft = draftContent[blockId]
      if (!draft) return
      setBusyBlockId(blockId)
      setEditorError(null)
      setEditorInfo(null)
      try {
        const next = writeBlockContent(block.content_json, {
          title: draft.title,
          body: draft.body,
        })
        await api.site.updateBlock(blockId, { content_json: next })
        setEditorInfo(`Saved ${block.block_type} block`)
        await refreshBlocks()
      } catch (err) {
        setEditorError(describeError(err))
      } finally {
        setBusyBlockId(null)
      }
    },
    [blocks, draftContent, refreshBlocks],
  )

  const onMove = useCallback(
    async (blockId: string, direction: -1 | 1) => {
      if (!selectedPageId) return
      const block = blocks.find((b) => b.id === blockId)
      if (!block) return
      const newOrder = block.block_order + direction
      if (newOrder < 0) return
      setBusyBlockId(blockId)
      setEditorError(null)
      try {
        await api.site.reorderBlock({
          pageId: selectedPageId,
          blockId,
          newOrder,
        })
        await refreshBlocks()
      } catch (err) {
        setEditorError(describeError(err))
      } finally {
        setBusyBlockId(null)
      }
    },
    [blocks, refreshBlocks, selectedPageId],
  )

  const onDelete = useCallback(
    async (blockId: string) => {
      const block = blocks.find((b) => b.id === blockId)
      if (!block) return
      const confirmed = window.confirm(`Delete ${block.block_type} block? This cannot be undone.`)
      if (!confirmed) return
      setBusyBlockId(blockId)
      setEditorError(null)
      try {
        await api.site.deleteBlock(blockId)
        setEditorInfo(`Deleted ${block.block_type} block`)
        await refreshBlocks()
      } catch (err) {
        setEditorError(describeError(err))
      } finally {
        setBusyBlockId(null)
      }
    },
    [blocks, refreshBlocks],
  )

  const onAdd = useCallback(async () => {
    if (!selectedPageId || adding) return
    setAdding(true)
    setEditorError(null)
    setEditorInfo(null)
    try {
      await api.site.createBlock({
        pageId: selectedPageId,
        blockType: addingType,
        contentJson: { title: '', body: '' },
        insertAt: blocks.length,
      })
      setEditorInfo(`Added ${addingType} block`)
      await refreshBlocks()
    } catch (err) {
      setEditorError(describeError(err))
    } finally {
      setAdding(false)
    }
  }, [adding, addingType, blocks.length, refreshBlocks, selectedPageId])

  const onPublish = useCallback(async () => {
    if (!site?.id || publishing) return
    setPublishing(true)
    setPublishMessage(null)
    try {
      // Default provider is 'manual' so live mode creates a real
      // sq_site_deployments row without inventing a synthetic
      // preview URL. Operators can later dispatch a real provider
      // from Hosting or wire Vercel directly.
      const result = await api.site.publish({ siteId: site.id, provider: 'manual' })
      setPublishMessage(
        `${result.dispatch.status === 'deployed' ? 'Published' : 'Deployment queued'} — provider ${result.deployment.provider}, status ${result.dispatch.status}` +
          (result.dispatch.deploy_url ? ` · ${result.dispatch.deploy_url}` : '') +
          (result.dispatch.error ? ` · ${result.dispatch.error}` : ''),
      )
      await refreshDeployments()
    } catch (err) {
      setPublishMessage(`Publish failed — ${describeError(err)}`)
    } finally {
      setPublishing(false)
    }
  }, [publishing, refreshDeployments, site?.id])

  useEffect(() => {
    setSelectedPageId((current) => {
      if (current && pages.some((page) => page.id === current)) return current
      return pages[0]?.id
    })
  }, [pages])

  useEffect(() => {
    let cancelled = false
    if (!site?.id) {
      setAssetCount(null)
      setDeploymentCount(null)
      setTotalBlockCount(null)
      return
    }

    void (async () => {
      try {
        const [assets, deployments] = await Promise.all([
          api.site.assets(site.id, 200),
          api.site.deployments(site.id, 50),
        ])
        if (cancelled) return
        setAssetCount(assets.assets?.length ?? 0)
        setDeploymentCount(deployments.deployments?.length ?? 0)
      } catch {
        if (cancelled) return
        setAssetCount(null)
        setDeploymentCount(null)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [site?.id])

  useEffect(() => {
    let cancelled = false
    if (!site?.id || pages.length === 0) {
      setTotalBlockCount(pages.length === 0 ? 0 : null)
      return
    }

    void (async () => {
      try {
        const all = await Promise.all(pages.map((page) => api.site.blocks(page.id)))
        if (cancelled) return
        setTotalBlockCount(
          all.reduce((sum, result) => sum + (result.blocks?.length ?? 0), 0),
        )
      } catch {
        if (cancelled) return
        setTotalBlockCount(null)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [pages, site?.id])

  const totalBlocks = useMemo(() => totalBlockCount ?? 0, [totalBlockCount])

  return (
    <ProjectSurface
      kicker="PROJECT · SITE BUILDER"
      title="Site Builder"
      description="Pages, blocks, assets, preview, and publish — backed by the hierarchical sq_* site schema."
    >
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
          marginBottom: 22,
        }}
      >
        <MetricCard label="Pages" value={pages.length} sub={pages.length ? 'ordered tree loaded' : 'no pages yet'} />
        <MetricCard label="Blocks" value={totalBlocks} sub="selected page" />
        <MetricCard label="Assets" value={assetCount ?? '—'} sub="images · files" />
        <MetricCard label="Deployments" value={deploymentCount ?? '—'} sub="site deploy history" />
      </section>

      <div
        style={{
          ...text.kicker,
          fontSize: 9,
          letterSpacing: '0.24em',
          color: SHELL_TOKENS.inkWhisper,
          marginBottom: 10,
        }}
      >
        SITE TREE
      </div>

      {loading ? (
        <EmptyState title="Loading site" body="Reading sq_sites." />
      ) : error ? (
        <EmptyState title="Site unavailable" body={error} />
      ) : !site ? (
        <EmptyState
          title="No site initialized"
          body={
            <>
              This project does not have a site row in `sq_sites` yet.
              <div style={{ marginTop: 14 }}>
                <button
                  type="button"
                  onClick={() => void initialize()}
                  disabled={creating}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: `1px solid ${SHELL_TOKENS.brandLine}`,
                    background: SHELL_TOKENS.brandSoft,
                    color: SHELL_TOKENS.brand,
                    cursor: creating ? 'not-allowed' : 'pointer',
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontFamily: 'var(--font-mono)',
                    opacity: creating ? 0.6 : 1,
                  }}
                >
                  {creating ? 'Initializing…' : 'Initialize Site'}
                </button>
              </div>
            </>
          }
        />
      ) : pagesLoading ? (
        <EmptyState title="Loading pages" body="Reading sq_site_pages." />
      ) : pagesError ? (
        <EmptyState title="Pages unavailable" body={pagesError} />
      ) : pages.length === 0 ? (
        <EmptyState
          title="No pages yet"
          body="This site exists, but there are no sq_site_pages rows yet."
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(260px, 320px) minmax(0, 1fr)',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pages.map((page) => {
              const active = page.id === selectedPageId
              return (
                <button
                  key={page.id}
                  type="button"
                  onClick={() => setSelectedPageId(page.id)}
                  style={{
                    textAlign: 'left',
                    padding: '14px 16px',
                    borderRadius: 10,
                    background: active ? SHELL_TOKENS.brandSoft : SHELL_TOKENS.surface,
                    border: active
                      ? `1px solid ${SHELL_TOKENS.brandLine}`
                      : `1px solid ${SHELL_TOKENS.border}`,
                    color: active ? SHELL_TOKENS.brand : SHELL_TOKENS.ink,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{page.title}</div>
                  <div
                    style={{
                      fontSize: 10,
                      marginTop: 4,
                      fontFamily: 'var(--font-mono)',
                      color: active ? SHELL_TOKENS.brand : SHELL_TOKENS.inkMuted,
                    }}
                  >
                    {page.path} · order {page.page_order}
                  </div>
                </button>
              )
            })}
          </div>

          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  ...text.kicker,
                  fontSize: 9,
                  letterSpacing: '0.24em',
                  color: SHELL_TOKENS.inkWhisper,
                }}
              >
                BLOCKS
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <select
                  value={addingType}
                  onChange={(e) => setAddingType(e.target.value)}
                  style={selectStyle}
                  disabled={adding}
                >
                  {BLOCK_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => void onAdd()}
                  disabled={adding}
                  style={primaryButtonStyle(adding)}
                >
                  {adding ? 'Adding…' : '+ Add block'}
                </button>
                <button
                  type="button"
                  onClick={() => void onPublish()}
                  disabled={publishing}
                  style={primaryButtonStyle(publishing)}
                >
                  {publishing ? 'Publishing…' : 'Publish'}
                </button>
              </div>
            </div>

            {(editorError || editorInfo || publishMessage) && (
              <div
                style={{
                  marginBottom: 10,
                  padding: '8px 12px',
                  borderRadius: 8,
                  background: editorError
                    ? 'rgba(239,68,68,0.10)'
                    : SHELL_TOKENS.brandSoft,
                  border: `1px solid ${
                    editorError ? 'rgba(239,68,68,0.30)' : SHELL_TOKENS.brandLine
                  }`,
                  color: editorError ? SHELL_TOKENS.red : SHELL_TOKENS.brand,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                }}
              >
                {editorError || publishMessage || editorInfo}
              </div>
            )}

            {blocksLoading ? (
              <EmptyState title="Loading blocks" body="Reading sq_site_blocks." />
            ) : blocksError ? (
              <EmptyState title="Blocks unavailable" body={blocksError} />
            ) : blocks.length === 0 ? (
              <EmptyState
                title="No blocks on this page"
                body="The selected page has no sq_site_blocks rows yet. Add one with the control above."
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {blocks.map((block, index) => {
                  const draft = draftContent[block.id] ?? { title: '', body: '' }
                  const busy = busyBlockId === block.id
                  const original = readBlockContent(block.content_json)
                  const dirty =
                    draft.title !== original.title || draft.body !== original.body
                  return (
                    <div
                      key={block.id}
                      style={{
                        padding: '14px 16px',
                        borderRadius: 10,
                        background: SHELL_TOKENS.surface,
                        border: `1px solid ${SHELL_TOKENS.border}`,
                        opacity: busy ? 0.6 : 1,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 8,
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: SHELL_TOKENS.ink,
                            }}
                          >
                            {block.block_type}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              marginTop: 4,
                              fontFamily: 'var(--font-mono)',
                              color: SHELL_TOKENS.inkMuted,
                            }}
                          >
                            order {block.block_order} · {block.visibility}
                            {block.locked ? ' · locked' : ''}
                            {typeof block.asset_count === 'number'
                              ? ` · ${block.asset_count} assets`
                              : ''}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            type="button"
                            onClick={() => void onMove(block.id, -1)}
                            disabled={busy || index === 0}
                            style={iconButtonStyle(busy || index === 0)}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => void onMove(block.id, 1)}
                            disabled={busy || index === blocks.length - 1}
                            style={iconButtonStyle(busy || index === blocks.length - 1)}
                            title="Move down"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() => void onDelete(block.id)}
                            disabled={busy}
                            style={iconButtonStyle(busy)}
                            title="Delete block"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <input
                        type="text"
                        value={draft.title}
                        placeholder="title"
                        onChange={(e) =>
                          setDraftContent((prev) => ({
                            ...prev,
                            [block.id]: { ...draft, title: e.target.value },
                          }))
                        }
                        disabled={busy}
                        style={inputStyle}
                      />
                      <textarea
                        value={draft.body}
                        placeholder="body / copy"
                        onChange={(e) =>
                          setDraftContent((prev) => ({
                            ...prev,
                            [block.id]: { ...draft, body: e.target.value },
                          }))
                        }
                        disabled={busy}
                        rows={3}
                        style={{ ...inputStyle, marginTop: 6, resize: 'vertical' }}
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: 8,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => void onUpdateContent(block.id)}
                          disabled={busy || !dirty}
                          style={primaryButtonStyle(busy || !dirty)}
                        >
                          {busy ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </ProjectSurface>
  )
}

const inputStyle: CSSProperties = {
  width: '100%',
  borderRadius: 8,
  border: `1px solid ${SHELL_TOKENS.border}`,
  background: SHELL_TOKENS.void,
  color: SHELL_TOKENS.ink,
  padding: '8px 10px',
  fontSize: 12,
  fontFamily: 'var(--font-mono)',
}

const selectStyle: CSSProperties = {
  borderRadius: 8,
  border: `1px solid ${SHELL_TOKENS.border}`,
  background: SHELL_TOKENS.void,
  color: SHELL_TOKENS.ink,
  padding: '7px 10px',
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
}

function primaryButtonStyle(disabled: boolean): CSSProperties {
  return {
    padding: '7px 12px',
    borderRadius: 8,
    border: `1px solid ${SHELL_TOKENS.brandLine}`,
    background: SHELL_TOKENS.brandSoft,
    color: SHELL_TOKENS.brand,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontFamily: 'var(--font-mono)',
    opacity: disabled ? 0.55 : 1,
  }
}

function iconButtonStyle(disabled: boolean): CSSProperties {
  return {
    width: 26,
    height: 26,
    borderRadius: 6,
    border: `1px solid ${SHELL_TOKENS.border}`,
    background: SHELL_TOKENS.void,
    color: SHELL_TOKENS.inkDim,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontFamily: 'var(--font-mono)',
    opacity: disabled ? 0.4 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
}
